// Supabase browser client. Auth only: the SPA never queries Postgres directly.
//
// This matters and is worth stating plainly. The database has 252 tables, 129
// views and 74 RLS policies for 95 RLS-enabled tables, and a Postgres view
// bypasses RLS entirely unless it was created with `security_invoker = true`.
// Pointing an authenticated browser client at those tables today would leak one
// driver's earnings and disciplinary record to another. So the browser gets a
// session token and nothing else; every read and write goes through the Worker,
// which connects as `cockpit_api` and re-checks capabilities on every call.
//
// When the driver and owner portals are built, they get a purpose-built
// `security_invoker` read surface of their own. They do NOT get the anon key
// pointed at these tables. See docs/DRIVER-AUTH.md.
import { createClient } from '@supabase/supabase-js'

const url = import.meta.env.VITE_SUPABASE_URL ?? ''
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY ?? ''

/**
 * True when the build was made without the two public Vite variables set.
 * Surfaced by AuthGate as a readable screen.
 *
 * WHY THIS IS A FLAG AND NOT A `throw`:
 *
 * The first version of this file threw at module scope. Vite inlines
 * `import.meta.env.*` at build time, so with the variables absent the condition
 * folded to a constant and rolldown eliminated everything after the throw as
 * dead code. The build reported success, and produced a 1 KB entry chunk
 * containing no application at all: no sidebar, no pages, nothing. 2,622
 * modules transformed and none of them shipped.
 *
 * A build that succeeds while emitting an empty app is worse than a build that
 * fails, so the check moved to runtime where it cannot delete the program.
 * scripts/verify-build.mjs now also asserts that known application strings are
 * present in dist, so this cannot recur silently.
 */
export const missingSupabaseConfig = !url || !anonKey

export const supabase = createClient(
  url || 'https://placeholder.invalid',
  anonKey || 'placeholder-key',
  {
    auth: {
      persistSession: true,
      autoRefreshToken: true,
      detectSessionInUrl: true,
      flowType: 'pkce',
    },
    // No Postgres access from the browser. If a future feature needs it, add a
    // dedicated client here with an explicit comment about which RLS policies
    // make it safe.
    db: { schema: 'public' },
  },
)
